import React, { useEffect, useRef, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { stripeService } from '../services';
import gatheringService from '../services/gatheringService';
import { isStripeDevMode, mockConfirmCardSetup } from '../utils/stripeDevMode';
import DevCardElement from './DevCardElement';
import CardPaymentSection from './CardPaymentSection';
import { RingCta } from './gathering/gatheringUi';
import { SheetLines, SheetCard, SheetError } from './GatheringCheckoutForm';
import { dayLabel } from '../utils/surfaceFormat';

function formatCents(cents) {
  return `$${(Math.abs(cents || 0) / 100).toFixed(2)}`;
}

/**
 * GatheringEntryForm — pay the one-time gathering entry fee (the pass). The
 * application must already exist (created via gatheringService.apply); this just
 * quotes + confirms the charge. Card-only. Props: gatheringId, userEmail, onDone().
 */
export default function GatheringEntryForm({ gatheringId, userEmail, onDone, onError, gatheringName, partyStarter = false, fundingDeadline = null }) {
  const stripe = useStripe();
  const cardRef = useRef(null);
  const [cardReady, setCardReady] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [devBehavior, setDevBehavior] = useState('success');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const r = await gatheringService.applyQuote(gatheringId);
      if (cancelled) return;
      if (!r.success) setError(r.error?.message || r.error || 'Unable to load quote');
      else setQuote(r.data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [gatheringId]);

  async function pay(e) {
    e?.preventDefault?.();
    if (!quote) return;
    setSubmitting(true);
    setError(null);

    let stripeCustomerId = null;
    let stripePaymentMethodId = null;
    const needsCard = (quote.cardPortionCents || 0) > 0;

    if (needsCard) {
      if (isStripeDevMode()) {
        const mock = await mockConfirmCardSetup(devBehavior);
        stripePaymentMethodId = mock.setupIntent.payment_method;
        stripeCustomerId = `cus_dev_${gatheringId}`;
      } else {
        if (!stripe) { setSubmitting(false); setError('Stripe is not loaded'); return; }
        const setupRes = await stripeService.createSetupIntent();
        if (!setupRes.success) { setSubmitting(false); setError(setupRes.error || 'Could not prepare payment'); return; }
        if (!cardRef.current) { setSubmitting(false); setError('Card form is not ready'); return; }
        const result = await cardRef.current.confirmSetup(setupRes.data.clientSecret, { email: userEmail });
        if (result.error) { setSubmitting(false); setError(result.error); return; }
        stripePaymentMethodId = result.paymentMethod;
        stripeCustomerId = setupRes.data.customerId;
      }
    }

    let res = await gatheringService.applyConfirm(gatheringId, { stripeCustomerId, stripePaymentMethodId, skipBalance: true });
    // Native entry: a card that needs 3-D Secure comes back `requires_action` with the intent's
    // client secret. Confirm it here, then ask the server to settle the SAME attempt — never a
    // second charge.
    if (res.success && res.data?.status === 'requires_action' && res.data?.clientSecret && stripe && !isStripeDevMode()) {
      const action = await stripe.confirmCardPayment(res.data.clientSecret);
      if (action?.error) { setSubmitting(false); setError(action.error.message || 'Card authentication failed'); return; }
      res = await gatheringService.applyConfirm(gatheringId, { stripeCustomerId, stripePaymentMethodId, skipBalance: true });
    }
    setSubmitting(false);
    if (!res.success) { setError(res.error?.message || res.error || 'Payment failed'); onError?.(res.error); return; }
    if (res.data?.status && !['settled', 'paid', 'accepted'].includes(res.data.status)) {
      setError(res.data.status === 'failed' ? 'The card was declined. Try another card.' : 'The payment is still processing — check back in a moment.');
      return;
    }
    onDone?.(res.data);
  }

  if (loading) return <div className="font-mono" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.14em' }}>LOADING…</div>;
  if (!quote) return <SheetError>{error || 'Unable to load quote'}</SheetError>;

  const total = quote.cardOnlyTotalCents || quote.grandTotalCents || 0;
  const onTop = (quote.prServiceFeeCents || 0) + (quote.gstCents || 0) + (quote.stripeFeeCents || 0);
  const rows = [{ key: 'entry', title: 'Gathering entry', sub: `One-time${gatheringName ? ` · ${gatheringName}` : ''}`, value: formatCents(quote.ticketCents) }];
  if (onTop > 0) rows.push({ key: 'fees', title: 'PR service fee + GST + processing', sub: 'On top of the entry', value: formatCents(onTop) });

  return (
    <form onSubmit={pay} className="flex flex-col" style={{ gap: 16 }}>
      <SheetLines rows={rows} total={formatCents(total)} testId="entry-lines" />
      <SheetCard>
        {isStripeDevMode() ? (
          <DevCardElement value={devBehavior} onChange={setDevBehavior} />
        ) : (
          <CardPaymentSection ref={cardRef} amountCents={total} currency="cad" countryDefault="CA" onValidityChange={setCardReady} disabled={submitting} label={null} appearance="pill" />
        )}
      </SheetCard>
      {error && <SheetError>{error}</SheetError>}
      <RingCta type="submit" disabled={submitting || (!isStripeDevMode() && !cardReady)} data-testid="entry-pay">
        {submitting ? 'PROCESSING…' : `PAY ${formatCents(total)}`}
      </RingCta>
      <span className="font-mono" style={{ fontSize: 11, lineHeight: 1.6, color: '#6B7280' }}>
        {partyStarter
          ? `Held in escrow. If it doesn't reach the goal${fundingDeadline ? ` by ${dayLabel(fundingDeadline)}` : ''}, it comes back to you.`
          : 'Event tickets are separate. You still pick those.'}
      </span>
    </form>
  );
}
